import React, { useState, useEffect, useRef, useMemo } from 'react';
import ConfirmDialog from './ConfirmDialog';

const BrandPopup = ({ brand, regions, onClose, onSave, onDelete, loading }) => {
  const isNew = !brand;

  const [form, setForm] = useState({
    name:        brand?.name        || '',
    website_url: brand?.website_url || '',
    regionIds:   brand?.regions?.map(r => r.id) || [],
    logoFile:    null,
    logoPreview: null,
  });

  const [showUnsaved, setShowUnsaved]   = useState(false);
  const [showDelete,  setShowDelete]    = useState(false);
  const fileInputRef = useRef(null);

  const isDirty = useMemo(() => {
    if (isNew) {
      return form.name || form.website_url || form.regionIds.length > 0 || form.logoFile;
    }
    const originalIds = [...(brand?.regions?.map(r => r.id) || [])].sort().join(',');
    const currentIds  = [...form.regionIds].sort().join(',');
    return (
      form.name        !== (brand?.name        || '') ||
      form.website_url !== (brand?.website_url || '') ||
      currentIds       !== originalIds ||
      form.logoFile    !== null
    );
  }, [form, brand, isNew]);

  // Chiudi con ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  const handleClose = () => {
    if (isDirty) {
      setShowUnsaved(true);
    } else {
      onClose();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      alert('Formato non supportato. Usa PNG, JPG o SVG.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File troppo grande. Massimo 5 MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setForm(f => ({ ...f, logoFile: file, logoPreview: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const toggleRegion = (id) => {
    setForm(f => ({
      ...f,
      regionIds: f.regionIds.includes(id)
        ? f.regionIds.filter(r => r !== id)
        : [...f.regionIds, id],
    }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      alert('Il nome è obbligatorio.');
      return;
    }
    if (form.regionIds.length === 0) {
      alert('Seleziona almeno una regione.');
      return;
    }

    const fd = new FormData();
    fd.append('name', form.name.trim());
    fd.append('website_url', form.website_url.trim());
    fd.append('region_ids', JSON.stringify(form.regionIds));
    if (form.logoFile) fd.append('logo', form.logoFile);

    onSave(fd);
  };

  const currentLogo = brand?.logo || null;

  return (
    <>
      <div
        className="fixed inset-0 z-[9990] bg-black/75 flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      >
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
            <h2 className="text-white font-semibold text-lg">
              {isNew ? 'Nuovo Brand' : 'Modifica Brand'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition text-xl leading-none"
              aria-label="Chiudi"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">
            {/* Nome */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Nome brand *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white transition"
                placeholder="Es. Brooks"
              />
            </div>

            {/* Sito web */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Sito web</label>
              <input
                type="url"
                value={form.website_url}
                onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white transition"
                placeholder="https://..."
              />
            </div>

            {/* Logo */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Logo</label>
              <div className="flex flex-wrap gap-4 items-start">
                {/* Logo attuale */}
                {currentLogo && (
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-500">Attuale</span>
                    <div className="w-28 h-20 bg-black border border-gray-700 rounded-lg flex items-center justify-center p-2">
                      <img
                        src={currentLogo}
                        alt="Logo attuale"
                        className="w-full h-full object-contain grayscale"
                      />
                    </div>
                  </div>
                )}

                {/* Anteprima nuovo */}
                {form.logoPreview && (
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-500">Nuovo</span>
                    <div className="w-28 h-20 bg-black border border-gray-700 rounded-lg flex items-center justify-center p-2">
                      <img
                        src={form.logoPreview}
                        alt="Anteprima"
                        className="w-full h-full object-contain grayscale"
                      />
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2 justify-center mt-5">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 py-2 text-xs rounded-lg border border-gray-600 text-gray-300 hover:border-white hover:text-white transition"
                  >
                    {currentLogo ? 'Cambia logo' : 'Carica logo'}
                  </button>
                  <span className="text-xs text-gray-600">PNG, JPG, SVG — max 5 MB</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Regioni */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">
                Regioni * <span className="text-gray-600">(seleziona almeno una)</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {regions.filter(r => r.is_active).map(r => (
                  <label
                    key={r.id}
                    className="flex items-center gap-2 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={form.regionIds.includes(r.id)}
                      onChange={() => toggleRegion(r.id)}
                      className="w-4 h-4 accent-red-500"
                    />
                    <span className="text-sm text-gray-300 group-hover:text-white transition">
                      {r.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800 sticky bottom-0 bg-gray-900">
            <div>
              {!isNew && (
                <button
                  onClick={() => setShowDelete(true)}
                  className="text-xs px-3 py-2 rounded-lg border border-red-900 text-red-400 hover:border-red-500 hover:text-red-300 transition"
                >
                  Elimina brand
                </button>
              )}
            </div>
            <button
              onClick={handleSubmit}
              disabled={!isDirty || loading}
              className={`px-5 py-2 text-sm rounded-lg font-medium transition ${
                isDirty && !loading
                  ? 'bg-white text-black hover:bg-gray-100'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {loading ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </div>
      </div>

      {/* Dialogo modifiche non salvate */}
      {showUnsaved && (
        <ConfirmDialog
          message="Hai modifiche non salvate. Sei sicuro di voler uscire?"
          confirmLabel="Esci senza salvare"
          danger
          onConfirm={onClose}
          onCancel={() => setShowUnsaved(false)}
        />
      )}

      {/* Dialogo elimina */}
      {showDelete && (
        <ConfirmDialog
          message={`Sei sicuro di voler eliminare il brand "${brand?.name}"? L'operazione è irreversibile.`}
          confirmLabel="Elimina"
          danger
          onConfirm={() => { setShowDelete(false); onDelete(); }}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </>
  );
};

export default BrandPopup;
